import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircleDollarSign, TrendingUp, Receipt, CalendarX, User, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { closeDay, clearError } from '../../redux/slices/shiftSlice';

const formatRs = (n) => `Rs. ${(Number(n) || 0).toLocaleString()}`;

export default function SaleEndModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { summary, closing } = useSelector((state) => state.shift);

  const users = summary?.users || [];
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'admin';

  if (!isAdmin) return null;

  const handleConfirm = async () => {
    const result = await dispatch(closeDay());
    if (closeDay.fulfilled.match(result)) {
      toast.success("Today's sale has ended. Business day closed and daily report generated.");
      dispatch(clearError());
      onClose();
    } else {
      toast.error(result.payload || 'Failed to end today\'s sale');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!closing) onClose(); }}
      title="End Today's Sale?"
      description="Closing the business day finalizes today's sales and profit. No more sales can be recorded for today after this."
      size="md"
      closeOnBackdrop={!closing}
      showCloseButton={!closing}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
            <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-1.5">
              <CircleDollarSign size={14} className="text-blue-500" />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Sales</p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{formatRs(summary?.totalSales)}</p>
          </div>
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-1.5">
              <TrendingUp size={14} className="text-emerald-500" />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Profit</p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{formatRs(summary?.totalProfit)}</p>
          </div>
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
            <div className="w-7 h-7 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center mb-1.5">
              <Receipt size={14} className="text-purple-500" />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Orders</p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{summary?.totalOrders || 0}</p>
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            User Sales
          </h4>
          {users.length > 0 ? (
            <div className="space-y-2">
              {users.map((u) => (
                <div
                  key={u.userId}
                  className="flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-gray-700/40 rounded-lg"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <User size={14} className="text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{u.userName}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{u.orders || 0} orders</p>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatRs(u.sales)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">No user sales recorded today.</p>
          )}
        </div>

        <p className="flex items-start gap-2 text-xs text-gray-400 dark:text-gray-500">
          <Lock size={13} className="mt-0.5 flex-shrink-0" />
          <span>
            After confirming, today's session is marked CLOSED and closed-by will be recorded as{' '}
            <span className="font-medium text-gray-900 dark:text-white">{user?.name}</span>. Sales are blocked
            until you manually press <span className="font-medium text-gray-900 dark:text-white">START NEW DAY</span>.
          </span>
        </p>
      </div>

      <div className="flex gap-3 w-full mt-5">
        <Button variant="secondary" onClick={onClose} disabled={closing} className="flex-1">
          Cancel
        </Button>
        <Button variant="danger" loading={closing} icon={CalendarX} onClick={handleConfirm} className="flex-1">
          Confirm Sale End
        </Button>
      </div>
    </Modal>
  );
}